// src/pages/Login.jsx - Advanced Professional Login with Enhanced 2FA
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import generateEmailHTML from '../components/EmailTemplate';
import {
  FiMail,
  FiLock,
  FiAlertCircle,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiShield,
  FiSend,
  FiClock,
  FiKey,
  FiArrowLeft,
  FiSmartphone,
  FiRefreshCw,
  FiInfo,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================
// FIREBASE ERROR MAP
// =============================================

/**
 * Mapping of Firebase error codes to user-friendly messages
 */
const FIREBASE_ERROR_MAP = {
  'auth/user-not-found': {
    message: 'No account found with this email address.',
    type: 'error',
  },
  'auth/wrong-password': {
    message: 'Incorrect password. Please try again.',
    type: 'error',
  },
  'auth/invalid-email': {
    message: 'Please enter a valid email address.',
    type: 'error',
  },
  'auth/user-disabled': {
    message: 'This account has been disabled. Please contact support.',
    type: 'error',
  },
  'auth/too-many-requests': {
    message: 'Too many failed attempts. Please try again later.',
    type: 'error',
  },
  'auth/network-request-failed': {
    message: 'Network error. Please check your connection.',
    type: 'error',
  },
  'auth/invalid-credential': {
    message: 'Invalid credentials. Please check your email and password.',
    type: 'error',
  },
  'auth/email-already-in-use': {
    message: 'This email is already registered.',
    type: 'error',
  },
  'auth/weak-password': {
    message: 'Password should be at least 6 characters.',
    type: 'error',
  },
  'auth/requires-recent-login': {
    message: 'Please re-authenticate to perform this action.',
    type: 'error',
  },
  'auth/operation-not-allowed': {
    message: 'This sign-in method is not enabled. Please contact support.',
    type: 'error',
  },
  'auth/account-exists-with-different-credential': {
    message: 'An account already exists with this email address.',
    type: 'error',
  },
  'auth/expired-action-code': {
    message: 'The verification link has expired. Please request a new one.',
    type: 'error',
  },
  'auth/invalid-action-code': {
    message: 'Invalid verification link. Please request a new one.',
    type: 'error',
  },
  'auth/invalid-verification-code': {
    message: 'Invalid verification code. Please try again.',
    type: 'error',
  },
  'auth/missing-email': {
    message: 'Please enter your email address.',
    type: 'error',
  },
  'auth/missing-password': {
    message: 'Please enter your password.',
    type: 'error',
  },
  'auth/quota-exceeded': {
    message: 'Service quota exceeded. Please try again later.',
    type: 'error',
  },
  'auth/timeout': {
    message: 'Request timed out. Please try again.',
    type: 'error',
  },
  'auth/admin-restricted-operation': {
    message: 'This operation is restricted to administrators.',
    type: 'error',
  },
  'auth/invalid-api-key': {
    message: 'Invalid API key. Please contact support.',
    type: 'error',
  },
  'auth/app-not-authorized': {
    message: 'App is not authorized. Please contact support.',
    type: 'error',
  },
  'auth/internal-error': {
    message: 'Internal authentication error. Please try again.',
    type: 'error',
  },
  'auth/web-storage-unsupported': {
    message: 'Browser storage is not supported. Please use a modern browser.',
    type: 'error',
  },
  'auth/admin-required': {
    message: 'Access denied. Admin privileges required.',
    type: 'error',
  },
};

/**
 * Convert Firebase error to human-readable message
 * @param {Error|string} error - The error object or error code
 * @returns {object} Object with message and type
 */
const getHumanReadableError = error => {
  if (!error) return { message: 'An unknown error occurred.', type: 'error' };

  if (typeof error === 'string') {
    const firebaseError = FIREBASE_ERROR_MAP[error];
    if (firebaseError) return firebaseError;

    for (const [code, value] of Object.entries(FIREBASE_ERROR_MAP)) {
      if (error.includes(code)) return value;
    }
    return { message: error, type: 'error' };
  }

  if (error.code) {
    const firebaseError = FIREBASE_ERROR_MAP[error.code];
    if (firebaseError) return firebaseError;

    if (error.message) {
      for (const [code, value] of Object.entries(FIREBASE_ERROR_MAP)) {
        if (error.message.includes(code)) return value;
      }
    }
  }

  return {
    message: error?.message || 'Something went wrong. Please try again.',
    type: 'error',
  };
};

// =============================================
// MODERN GRADIENT BACKGROUND
// =============================================

/**
 * Animated gradient background component
 * Renders dynamic gradient orbs and particles on canvas
 */
const GradientBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width, height;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    const isDarkMode = () => {
      return document.documentElement.classList.contains('dark');
    };

    const drawGradient = time => {
      const isDark = isDarkMode();

      ctx.clearRect(0, 0, width, height);

      // Background gradient
      const bgGrad = ctx.createRadialGradient(
        width * 0.5,
        height * 0.5,
        0,
        width * 0.5,
        height * 0.5,
        Math.max(width, height) * 0.8,
      );

      if (isDark) {
        bgGrad.addColorStop(0, '#0a0a12');
        bgGrad.addColorStop(0.3, '#0d0d1a');
        bgGrad.addColorStop(0.6, '#0a0a15');
        bgGrad.addColorStop(1, '#05050a');
      } else {
        bgGrad.addColorStop(0, '#f8f9fc');
        bgGrad.addColorStop(0.3, '#f0f2f7');
        bgGrad.addColorStop(0.6, '#e8ecf3');
        bgGrad.addColorStop(1, '#dfe3ed');
      }
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Animated orbs
      const orbs = [
        {
          x: width * 0.2 + Math.sin(time * 0.0004) * width * 0.3,
          y: height * 0.2 + Math.cos(time * 0.0003) * height * 0.25,
          radius: Math.min(width, height) * 0.35,
          colors: isDark
            ? [
                'rgba(59, 130, 246, 0.06)',
                'rgba(139, 92, 246, 0.04)',
                'rgba(59, 130, 246, 0)',
              ]
            : [
                'rgba(59, 130, 246, 0.06)',
                'rgba(139, 92, 246, 0.04)',
                'rgba(59, 130, 246, 0)',
              ],
        },
        {
          x: width * 0.8 + Math.sin(time * 0.0005 + 2) * width * 0.25,
          y: height * 0.7 + Math.cos(time * 0.0004 + 1) * height * 0.2,
          radius: Math.min(width, height) * 0.3,
          colors: isDark
            ? [
                'rgba(139, 92, 246, 0.05)',
                'rgba(236, 72, 153, 0.03)',
                'rgba(139, 92, 246, 0)',
              ]
            : [
                'rgba(139, 92, 246, 0.05)',
                'rgba(236, 72, 153, 0.03)',
                'rgba(139, 92, 246, 0)',
              ],
        },
        {
          x: width * 0.5 + Math.sin(time * 0.0006 + 3) * width * 0.2,
          y: height * 0.5 + Math.cos(time * 0.0005 + 4) * height * 0.15,
          radius: Math.min(width, height) * 0.25,
          colors: isDark
            ? [
                'rgba(236, 72, 153, 0.04)',
                'rgba(59, 130, 246, 0.02)',
                'rgba(236, 72, 153, 0)',
              ]
            : [
                'rgba(236, 72, 153, 0.04)',
                'rgba(59, 130, 246, 0.02)',
                'rgba(236, 72, 153, 0)',
              ],
        },
      ];

      for (const orb of orbs) {
        const grad = ctx.createRadialGradient(
          orb.x,
          orb.y,
          0,
          orb.x,
          orb.y,
          orb.radius,
        );
        grad.addColorStop(0, orb.colors[0]);
        grad.addColorStop(0.5, orb.colors[1]);
        grad.addColorStop(1, orb.colors[2]);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Particles
      const particleCount = isDark ? 80 : 50;
      for (let i = 0; i < particleCount; i++) {
        const seed = i * 137.508;
        const px = (Math.sin(time * 0.0003 + seed) * 0.5 + 0.5) * width;
        const py = (Math.cos(time * 0.0002 + seed * 1.3) * 0.5 + 0.5) * height;
        const size = Math.sin(time * 0.001 + seed * 2) * 1 + 1.5;
        const opacity = Math.sin(time * 0.0005 + seed * 3) * 0.5 + 0.5;

        ctx.beginPath();
        ctx.arc(px, py, size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(255, 255, 255, ${opacity * 0.03})`
          : `rgba(100, 120, 150, ${opacity * 0.03})`;
        ctx.fill();
      }

      // Grid lines
      const gridSpacing = 80;
      ctx.strokeStyle = isDark
        ? 'rgba(255, 255, 255, 0.008)'
        : 'rgba(0, 0, 0, 0.01)';
      ctx.lineWidth = 0.5;

      for (let x = 0; x < width; x += gridSpacing) {
        const offsetX = Math.sin(time * 0.0002 + x * 0.01) * 2;
        ctx.beginPath();
        ctx.moveTo(x + offsetX, 0);
        ctx.lineTo(x + offsetX, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSpacing) {
        const offsetY = Math.cos(time * 0.0002 + y * 0.01) * 2;
        ctx.beginPath();
        ctx.moveTo(0, y + offsetY);
        ctx.lineTo(width, y + offsetY);
        ctx.stroke();
      }

      // Vignette effect
      const vignette = ctx.createRadialGradient(
        width * 0.5,
        height * 0.5,
        0,
        width * 0.5,
        height * 0.5,
        Math.max(width, height) * 0.8,
      );
      vignette.addColorStop(0, isDark ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0)');
      vignette.addColorStop(0.7, isDark ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0)');
      vignette.addColorStop(1, isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, width, height);
    };

    const animate = () => {
      timeRef.current++;
      drawGradient(timeRef.current);
      animationRef.current = requestAnimationFrame(animate);
    };

    resize();
    animate();

    const observer = new MutationObserver(() => {});
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const handleResize = () => {
      resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full"
      style={{ zIndex: 0 }}
    />
  );
};

// =============================================
// VERIFICATION CODE INPUT
// =============================================

/**
 * Verification code input component with 6 individual digit inputs
 * Supports paste, auto-advance, and backspace navigation
 */
const VerificationCodeInput = ({
  code,
  setCode,
  isVerifying,
  error,
  onResend,
  timer,
  expiresAt,
}) => {
  const inputRefs = useRef([]);
  const [timeLeft, setTimeLeft] = useState(null);

  // Calculate time left until expiry
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimeLeft = () => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );
      setTimeLeft(remaining);
      if (remaining === 0) {
        setTimeLeft(0);
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  /**
   * Handle input change for a single digit
   * Auto-advances to next input when a digit is entered
   */
  const handleChange = (index, value) => {
    // Handle paste of multiple digits
    if (value.length > 1) {
      const chars = value.split('');
      chars.forEach((char, i) => {
        if (i < 6) {
          const newCode = [...code];
          newCode[index + i] = char;
          setCode(newCode);
          if (index + i < 5 && char) {
            inputRefs.current[index + i + 1]?.focus();
          }
        }
      });
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Handle backspace key to navigate to previous input
   */
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Handle paste event to fill all digits at once
   */
  const handlePaste = e => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const chars = pastedData.split('');
    const newCode = [...code];
    chars.forEach((char, i) => {
      if (i < 6) {
        newCode[i] = char;
      }
    });
    setCode(newCode);
    const lastFilledIndex = chars.length - 1;
    if (lastFilledIndex < 5) {
      inputRefs.current[lastFilledIndex + 1]?.focus();
    }
  };

  /**
   * Format seconds to MM:SS format
   */
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Expiry timer */}
      {expiresAt && timeLeft !== null && timeLeft > 0 && (
        <div
          className="flex items-center justify-center gap-2 text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <FiClock className="w-3 h-3" />
          <span>
            Code expires in{' '}
            <strong style={{ color: 'var(--text)' }}>
              {formatTime(timeLeft)}
            </strong>
          </span>
        </div>
      )}

      {timeLeft === 0 && expiresAt && (
        <div className="flex items-center justify-center gap-2 text-xs text-red-500">
          <FiAlertCircle className="w-3 h-3" />
          <span>Code has expired. Please request a new one.</span>
        </div>
      )}

      {/* Digit inputs */}
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {[0, 1, 2, 3, 4, 5].map(index => (
          <motion.input
            key={index}
            ref={el => (inputRefs.current[index] = el)}
            type="text"
            maxLength={1}
            value={code[index] || ''}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            disabled={isVerifying}
            className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl sm:text-3xl font-bold rounded-xl outline-none transition-all duration-300 focus:ring-2 focus:ring-blue-500 bg-transparent"
            style={{
              border: `2px solid ${
                error
                  ? '#ef4444'
                  : code[index]
                    ? 'rgba(var(--text-rgb), 0.3)'
                    : 'var(--border)'
              }`,
              color: 'var(--text)',
              backgroundColor: code[index]
                ? 'rgba(var(--border-rgb), 0.1)'
                : 'transparent',
            }}
            autoFocus={index === 0}
            whileFocus={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          />
        ))}
      </div>

      {/* Error message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-center gap-2 text-sm text-red-500"
        >
          <FiAlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Resend button */}
      <div className="flex flex-col items-center gap-2">
        <button
          onClick={onResend}
          disabled={timer > 0 || isVerifying}
          className="text-sm transition-colors hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          style={{ color: 'var(--muted)' }}
        >
          {timer > 0 ? (
            <>
              <FiRefreshCw className="w-4 h-4 animate-spin" />
              Resend code in {timer}s
            </>
          ) : (
            <>
              <FiSend className="w-4 h-4" />
              Resend verification code
            </>
          )}
        </button>
      </div>

      {/* Help text */}
      <div
        className="flex items-center justify-center gap-2 text-xs"
        style={{ color: 'var(--muted)' }}
      >
        <FiInfo className="w-3 h-3" />
        <span>Check your spam folder if you don't see the email</span>
      </div>
    </div>
  );
};

// =============================================
// SMOOTH ANIMATED INPUT
// =============================================

/**
 * Animated input component with floating label and smooth transitions
 */
const SmoothAnimatedInput = ({
  error,
  success,
  isFocused,
  icon: Icon,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  type = 'text',
  disabled = false,
  autoComplete,
  name,
  required = false,
  showPasswordToggle = false,
  onTogglePassword,
  showPassword = false,
  label,
}) => {
  return (
    <div className="relative">
      {label && (
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: 'var(--text)' }}
        >
          {label}
        </label>
      )}

      {isFocused && (
        <motion.div
          className="absolute -inset-0.5 rounded-xl z-0"
          style={{
            background: `linear-gradient(135deg, var(--text) 0%, transparent 50%, var(--text) 100%)`,
            opacity: 0.04,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.04 }}
          transition={{ duration: 0.8 }}
        />
      )}

      <div className="relative z-10">
        <div className="relative">
          <div
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 transition-colors duration-500"
            style={{
              color: isFocused ? 'var(--text)' : 'var(--muted)',
            }}
          >
            <Icon className="w-4 h-4" />
          </div>

          <motion.input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            required={required}
            disabled={disabled}
            className="w-full pl-11 pr-12 py-3 rounded-xl outline-none transition-all duration-500 focus:ring-0 bg-transparent relative z-10"
            style={{
              border: `1px solid ${
                error
                  ? '#ef4444'
                  : isFocused
                    ? 'rgba(var(--text-rgb), 0.12)'
                    : 'var(--border)'
              }`,
              color: 'var(--text)',
              transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
              boxShadow: isFocused ? '0 0 30px rgba(0,0,0,0.02)' : 'none',
            }}
            placeholder={placeholder}
            autoComplete={autoComplete}
            whileFocus={{ scale: 1.005 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />

          {showPasswordToggle && (
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 transition-colors hover:opacity-70"
              style={{ color: 'var(--muted)' }}
              disabled={disabled}
            >
              {showPassword ? (
                <FiEyeOff className="w-4 h-4" />
              ) : (
                <FiEye className="w-4 h-4" />
              )}
            </button>
          )}

          {success && !error && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20"
            >
              <FiCheckCircle className="w-5 h-5 text-green-500" />
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20"
            >
              <FiAlertCircle className="w-5 h-5 text-red-500" />
            </motion.div>
          )}
        </div>
      </div>

      {/* Animated bottom line */}
      <div className="absolute -bottom-0.5 left-4 right-4 h-px overflow-hidden rounded-full z-10">
        <motion.div
          className="h-full rounded-full"
          style={{
            backgroundColor: error ? '#ef4444' : 'var(--text)',
            opacity: isFocused ? 0.15 : 0.02,
          }}
          animate={{
            x: isFocused ? ['-100%', '0%', '100%', '0%', '100%'] : '0%',
          }}
          transition={{
            duration: 12,
            repeat: isFocused ? Infinity : 0,
            ease: 'easeInOut',
          }}
        />
      </div>
    </div>
  );
};

// =============================================
// LOGIN PAGE
// =============================================

/**
 * Main Login component with 2FA verification
 */
function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, currentUser, isAdmin, loading: authLoading } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);

  // 2FA State
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [verificationId, setVerificationId] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationExpiresAt, setVerificationExpiresAt] = useState(null);
  const [isResending, setIsResending] = useState(false);

  // Redirect if already logged in as admin
  useEffect(() => {
    if (!authLoading && currentUser && isAdmin) {
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    }
  }, [currentUser, isAdmin, authLoading, navigate, location]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  // Rate limiting: Reset after 5 minutes
  useEffect(() => {
    if (isRateLimited) {
      const timer = setTimeout(
        () => {
          setIsRateLimited(false);
          setLoginAttempts(0);
        },
        5 * 60 * 1000,
      );
      return () => clearTimeout(timer);
    }
  }, [isRateLimited]);

  // =============================================
  // GENERATE EMAIL HTML
  // =============================================

  /**
   * Generate email HTML for verification code
   * @param {string} code - The verification code
   * @param {string} type - The verification type (login or password)
   * @returns {string} HTML email content
   */
  const generateEmailHTMLForEmail = useCallback((code, type = 'login') => {
    return generateEmailHTML({
      code: code,
      type: type,
      title:
        type === 'password'
          ? 'Password Reset Verification'
          : 'Admin Login Verification',
      description:
        type === 'password'
          ? 'You have requested to reset your admin password. Please use the verification code below to confirm this action.'
          : 'You are attempting to sign in to the WD Hejbul admin dashboard. Please use the verification code below to complete your login.',
      expiresInMinutes: 10,
      logoUrl:
        'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1778039977/Fabicon-icon-of-wdhejbul_zrboh7.jpg',
    });
  }, []);

  // =============================================
  // VERIFICATION CODE HANDLERS
  // =============================================

  /**
   * Verify the entered code with the server
   */
  const verifyCode = useCallback(async () => {
    const code = verificationCode.join('');

    if (code.length !== 6) {
      setVerificationError('Please enter all 6 digits');
      return;
    }

    if (!verificationId) {
      setVerificationError('Verification session expired. Please try again.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verifyLoginCode',
          verificationId: verificationId,
          verificationCode: code,
          email: verificationEmail,
        }),
      });

      const data = await response.json();

      // Handle invalid code (401)
      if (response.status === 401) {
        if (data.remainingAttempts !== undefined) {
          setVerificationError(
            `Invalid verification code. ${data.remainingAttempts} attempts remaining.`,
          );
        } else {
          setVerificationError(
            data.error || 'Invalid verification code. Please try again.',
          );
        }
        setVerificationCode(['', '', '', '', '', '']);
        setTimeout(() => {
          document.querySelector('input[type="text"]')?.focus();
        }, 100);
        return;
      }

      // Handle other errors
      if (!data.success) {
        throw new Error(data.error || 'Verification failed');
      }

      // Success - proceed with login
      setSuccess('Verification successful! Logging in...');
      setVerificationError('');
      setShowVerification(false);

      await login(email.trim(), password.trim(), rememberMe);
    } catch (err) {
      console.error('Verification error:', err);

      if (err.name === 'TypeError' || err.message.includes('Failed to fetch')) {
        setVerificationError(
          'Network error. Please check your connection and try again.',
        );
      } else {
        const errorInfo = getHumanReadableError(err);
        setVerificationError(
          errorInfo.message || 'Verification failed. Please try again.',
        );
      }

      setVerificationCode(['', '', '', '', '', '']);
      setTimeout(() => {
        document.querySelector('input[type="text"]')?.focus();
      }, 100);
    } finally {
      setIsVerifying(false);
    }
  }, [
    verificationCode,
    verificationId,
    verificationEmail,
    email,
    password,
    rememberMe,
    login,
  ]);

  /**
   * Request a verification code from the server
   */
  const requestVerificationCode = useCallback(
    async emailAddress => {
      try {
        // Generate temporary code for email HTML
        const tempCode = Math.floor(100000 + Math.random() * 900000).toString();
        const htmlContent = generateEmailHTMLForEmail(tempCode, 'login');

        const response = await fetch('/api/verify-admin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'requestLoginVerification',
            email: emailAddress,
            htmlContent: htmlContent,
          }),
        });

        const data = await response.json();

        if (!data.success) {
          if (data.code === 'USER_NOT_FOUND') {
            setError(
              'No account found with this email address. Please check and try again.',
            );
          } else if (data.code === 'ADMIN_REQUIRED') {
            setError(
              'Access denied. This email does not have admin privileges.',
            );
          } else {
            setError(data.error || 'Failed to send verification code');
          }
          throw new Error(data.error || 'Failed to send verification code');
        }

        // Store verification data from server
        setVerificationId(data.verificationId);
        setVerificationEmail(emailAddress);
        setVerificationExpiresAt(data.expiresAt);
        setResendTimer(60);
        setVerificationError('');
        setShowVerification(true);
        setSuccess('Verification code sent to your email!');
        setError('');

        // Focus first input after delay
        setTimeout(() => {
          const firstInput = document.querySelector('input[type="text"]');
          if (firstInput) {
            firstInput.focus();
          }
        }, 300);

        return data;
      } catch (err) {
        console.error('Request verification error:', err);

        if (
          err.name === 'TypeError' ||
          err.message.includes('Failed to fetch')
        ) {
          setError(
            'Network error. Please check your connection and try again.',
          );
        } else {
          const errorInfo = getHumanReadableError(err);
          setError(errorInfo.message);
        }
        throw err;
      }
    },
    [generateEmailHTMLForEmail],
  );

  /**
   * Resend verification code
   */
  const resendCode = useCallback(async () => {
    if (resendTimer > 0 || isResending) return;

    setIsResending(true);
    setVerificationError('');
    setVerificationCode(['', '', '', '', '', '']);

    try {
      const tempCode = Math.floor(100000 + Math.random() * 900000).toString();
      const htmlContent = generateEmailHTMLForEmail(tempCode, 'login');

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'resendLoginCode',
          email: email.trim(),
          htmlContent: htmlContent,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to resend code');
      }

      setVerificationId(data.verificationId);
      setVerificationExpiresAt(data.expiresAt);
      setResendTimer(60);
      setVerificationError('');
      setSuccess('New verification code sent!');
      setVerificationCode(['', '', '', '', '', '']);

      setTimeout(() => {
        const firstInput = document.querySelector('input[type="text"]');
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    } catch (err) {
      console.error('Resend error:', err);
      const errorInfo = getHumanReadableError(err);
      setVerificationError(errorInfo.message);
    } finally {
      setIsResending(false);
    }
  }, [resendTimer, isResending, generateEmailHTMLForEmail, email]);

  // =============================================
  // FORM HANDLERS
  // =============================================

  /**
   * Handle login form submission
   */
  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      setError('');
      setSuccess('');
      setLoading(true);

      // Rate limiting check
      if (isRateLimited) {
        setError(
          'Too many attempts. Please wait 5 minutes before trying again.',
        );
        setLoading(false);
        return;
      }

      if (!email.trim()) {
        setError('Please enter your email address.');
        setLoading(false);
        return;
      }

      if (!password.trim()) {
        setError('Please enter your password.');
        setLoading(false);
        return;
      }

      try {
        setLoginAttempts(prev => prev + 1);
        if (loginAttempts + 1 >= 5) {
          setIsRateLimited(true);
          setError('Too many failed attempts. Please wait 5 minutes.');
          setLoading(false);
          return;
        }

        await requestVerificationCode(email.trim());
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    },
    [email, password, loginAttempts, isRateLimited, requestVerificationCode],
  );

  /**
   * Handle input changes with error clearing
   */
  const handleInputChange = useCallback(
    setter => e => {
      setter(e.target.value);
      if (error) setError('');
      if (success) setSuccess('');
    },
    [error, success],
  );

  /**
   * Navigate back to login form
   */
  const goBackToLogin = useCallback(() => {
    setShowVerification(false);
    setVerificationCode(['', '', '', '', '', '']);
    setVerificationError('');
    setError('');
  }, []);

  // Memoized values
  const isCodeComplete = useMemo(() => {
    return verificationCode.join('').length === 6;
  }, [verificationCode]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      <GradientBackground />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
        style={{ zIndex: 2 }}
      >
        <div
          className="relative rounded-3xl overflow-hidden backdrop-blur-xl p-6 sm:p-8"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
            border: '1px solid rgba(var(--text-rgb), 0.05)',
            boxShadow:
              '0 8px 40px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.02)',
          }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
          </div>

          <div className="absolute top-4 right-4 z-20">
            <ThemeToggle />
          </div>

          <div className="relative z-10">
            {!showVerification ? (
              // =========================================
              // LOGIN FORM
              // =========================================
              <>
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                    style={{
                      backgroundColor: 'rgba(var(--border-rgb), 0.15)',
                      border: '1px solid rgba(var(--text-rgb), 0.04)',
                    }}
                  >
                    <FiShield className="w-4 h-4 text-blue-500" />
                    <span className="text-sm" style={{ color: 'var(--text)' }}>
                      Admin Access
                    </span>
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="text-3xl md:text-4xl font-bold mb-2 tracking-tight"
                    style={{ color: 'var(--text)' }}
                  >
                    Welcome Back
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="text-sm"
                    style={{ color: 'var(--muted)' }}
                  >
                    Sign in to manage your portfolio
                  </motion.p>
                </div>

                {/* Error/Success Messages */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-6 p-4 rounded-xl flex items-start gap-3"
                      style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.06)',
                        border: '1px solid rgba(239, 68, 68, 0.1)',
                      }}
                    >
                      <FiAlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-500">
                          {error}
                        </p>
                        <p
                          className="text-xs mt-0.5"
                          style={{ color: 'var(--muted)' }}
                        >
                          Please try again or contact support.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {success && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-6 p-4 rounded-xl flex items-start gap-3"
                      style={{
                        backgroundColor: 'rgba(34, 197, 94, 0.06)',
                        border: '1px solid rgba(34, 197, 94, 0.1)',
                      }}
                    >
                      <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-500">
                          {success}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <SmoothAnimatedInput
                    icon={FiMail}
                    type="email"
                    value={email}
                    onChange={handleInputChange(setEmail)}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    isFocused={isEmailFocused}
                    error={!!error}
                    success={false}
                    placeholder="admin@example.com"
                    autoComplete="email"
                    disabled={loading}
                    name="email"
                    required
                    label="Email Address"
                  />

                  <SmoothAnimatedInput
                    icon={FiLock}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handleInputChange(setPassword)}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    isFocused={isPasswordFocused}
                    error={!!error}
                    success={false}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={loading}
                    name="password"
                    required
                    showPasswordToggle
                    onTogglePassword={() => setShowPassword(!showPassword)}
                    showPassword={showPassword}
                    label="Password"
                  />

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={e => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded transition-all duration-200 group-hover:scale-110"
                        style={{
                          accentColor: 'var(--text)',
                          borderColor: 'var(--border)',
                        }}
                        disabled={loading}
                      />
                      <span
                        className="text-sm"
                        style={{ color: 'var(--muted)' }}
                      >
                        Remember me
                      </span>
                    </label>
                    <span
                      className="text-xs flex items-center gap-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiShield className="w-3 h-3" />
                      Secure
                    </span>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={!loading ? { scale: 1.01 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}
                    className="w-full py-3.5 rounded-xl font-medium transition-all duration-300 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: loading
                        ? 'var(--border)'
                        : 'var(--text)',
                      color: loading ? 'var(--muted)' : 'var(--bg)',
                    }}
                  >
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {loading ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: 'linear',
                            }}
                            className="w-4 h-4 border-2 border-t-transparent rounded-full"
                            style={{ borderColor: 'var(--bg)' }}
                          />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4" /> Sign In
                        </>
                      )}
                    </span>
                  </motion.button>
                </form>

                {/* Footer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 text-center"
                >
                  <p
                    className="text-xs flex items-center justify-center gap-2"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiShield className="w-3 h-3" />
                    This area is restricted to authorized administrators only.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-4 flex justify-center"
                >
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px]"
                    style={{
                      backgroundColor: 'rgba(var(--border-rgb), 0.15)',
                      border: '1px solid rgba(var(--text-rgb), 0.04)',
                      color: 'var(--muted)',
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    Secure Connection
                  </span>
                </motion.div>
              </>
            ) : (
              // =========================================
              // 2FA VERIFICATION
              // =========================================
              <>
                <motion.button
                  onClick={goBackToLogin}
                  className="flex items-center gap-2 text-sm transition-colors hover:opacity-70 mb-6"
                  style={{ color: 'var(--muted)' }}
                  whileHover={{ x: -4 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Back to login
                </motion.button>

                <div className="text-center mb-8">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                    style={{
                      backgroundColor: 'rgba(var(--border-rgb), 0.15)',
                      border: '1px solid rgba(var(--text-rgb), 0.04)',
                    }}
                  >
                    <FiKey className="w-4 h-4 text-blue-500" />
                    <span className="text-sm" style={{ color: 'var(--text)' }}>
                      Two-Factor Verification
                    </span>
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="text-2xl md:text-3xl font-bold mb-2 tracking-tight"
                    style={{ color: 'var(--text)' }}
                  >
                    Enter Verification Code
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="text-sm"
                    style={{ color: 'var(--muted)' }}
                  >
                    We sent a 6-digit code to{' '}
                    <strong style={{ color: 'var(--text)' }}>
                      {verificationEmail}
                    </strong>
                  </motion.p>
                </div>

                {/* Verification Error */}
                <AnimatePresence>
                  {verificationError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-4 p-3 rounded-xl flex items-center gap-2"
                      style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.06)',
                        border: '1px solid rgba(239, 68, 68, 0.1)',
                      }}
                    >
                      <FiAlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-red-500">
                        {verificationError}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Verification Code Input */}
                <VerificationCodeInput
                  code={verificationCode}
                  setCode={setVerificationCode}
                  isVerifying={isVerifying}
                  error={verificationError}
                  onResend={resendCode}
                  timer={resendTimer}
                  expiresAt={verificationExpiresAt}
                />

                {/* Verify Button */}
                <motion.button
                  onClick={verifyCode}
                  disabled={isVerifying || !isCodeComplete}
                  whileHover={
                    !isVerifying && isCodeComplete ? { scale: 1.01 } : {}
                  }
                  whileTap={
                    !isVerifying && isCodeComplete ? { scale: 0.98 } : {}
                  }
                  className="w-full py-3.5 rounded-xl font-medium transition-all duration-300 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                  style={{
                    backgroundColor:
                      isCodeComplete && !isVerifying
                        ? 'var(--text)'
                        : 'var(--border)',
                    color:
                      isCodeComplete && !isVerifying
                        ? 'var(--bg)'
                        : 'var(--muted)',
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isVerifying ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                          className="w-4 h-4 border-2 border-t-transparent rounded-full"
                          style={{ borderColor: 'var(--bg)' }}
                        />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="w-4 h-4" />
                        Verify & Login
                      </>
                    )}
                  </span>
                </motion.button>

                {/* Footer Info */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 text-center"
                >
                  <p
                    className="text-xs flex items-center justify-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiShield className="w-3 h-3" />
                    This code expires in 10 minutes for security
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-3 flex justify-center"
                >
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px]"
                    style={{
                      backgroundColor: 'rgba(var(--border-rgb), 0.1)',
                      border: '1px solid rgba(var(--text-rgb), 0.03)',
                      color: 'var(--muted)',
                    }}
                  >
                    <FiSmartphone className="w-3 h-3" />
                    Check your email
                  </span>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Global Styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default Login;
